// Util.ts

import DataMgr from "@/Fwk/DataMgr";
import {masterConst} from "@/Master/Const/MasterConst";
import CookieMgr from "@/Fwk/CookieMgr";
import {WebCode} from "@/app/types/WebCode";

/**
 * Util.ts
 *
 * 一个工具类，提供各种实用的静态方法和属性，用于日志记录、时间处理、类型检查等。
 */
export default class Util {

    //todo 上线后 需要把production去掉
    static isDev = process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'production';

    // 控制是否启用日志输出
    static get isLog(): boolean {
        if (this.isDev) return true;
        const log = CookieMgr.getCookie('isLog');
        return log === '1';
    };

    //控制是否输入SDK的日志
    static get isSdkLog(): boolean {
        const log = CookieMgr.getCookie('isSdkLog');
        return log === '1';
    };

    // 控制是否启用测试模式
    static get isTestMode(): boolean {
        if (this.isDev) return true;
        const log = CookieMgr.getCookie('isTestMode');
        return log === '1';
    };

    // 临时变量
    private static newUUID: string;

    static get isWindow(): boolean {
        // 检查是否在浏览器环境
        return !(typeof window === 'undefined');
    }

    static get isNavigator(): boolean {
        // 检查是否在浏览器环境
        return !(typeof window === 'undefined' || typeof navigator === 'undefined');
    }

    // 额外处理 iPadOS 13+ 的情况
    static get isIPadOS() {
        if (!Util.isNavigator) return false;
        return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    }

    // 判断是否为iOS设备
    static get isIOS() {
        if (!Util.isNavigator) return false;
        // 判断是否是iPhone、iPad或iPod，并且没有 MSStream
        const iosDevice = /iPhone|iPad|iPod/i.test(navigator.userAgent) && !(window as any).MSStream;
        return iosDevice || Util.isIPadOS;
    };


    // 判断是否为Android设备
    static get isAndroid(): boolean {
        if (!Util.isNavigator) return false;
        return /Android/i.test(navigator.userAgent)
    };

    // 初始化开始时间
    static get startTimeMill(): number {
        return new Date().getTime();
    };

    static get startTime(): number {
        return Math.floor(Date.now() / 1000);
    };

    // 获取活动时间（毫秒）
    static get activityTimeMill(): number {
        return this.localTimeStampMill - this.startTimeMill;
    }

    // 获取活动时间（秒）
    static get activityTime(): number {
        return this.localTimeStamp - this.startTime;
    }

    /**
     * 获取当前时间戳（秒）
     */
    static get localTimeStamp(): number {
        return Math.floor(Date.now() / 1000);
    }

    /**
     * 获取当前时间戳（毫秒）
     */
    static get localTimeStampMill(): number {
        return Date.now();
    }

    /**
     * 生成日志信息字符串
     * @param tag 标签
     * @param value 可变参数
     * @returns 日志信息字符串
     */
    static toLogMessage(tag: string, ...value: any[]): string {
        let str = `${tag}->`;
        value.forEach((val, i) => {
            str += i > 0 ? ", " : "";
            str += val !== null ? val.toString() : "null";
        });
        return str;
    }

    /**
     * 生成日志信息字符串，无标签
     * @param value 可变参数
     * @returns 日志信息字符串
     */
    static toLogMessage1(...value: any[]): string {
        let str = "";

        value.forEach((val, i) => {
            str += i > 0 ? ", " : "";
            str += val ? val.toString() : "null";
        });
        return str;
    }

    /**
     * 打印调试日志（仅当isLog为true时）
     * @param tag 标签
     * @param value 可变参数
     */
    static logDebug(tag: string, ...value: any[]): void {
        if (this.isLog) {
            const times = `${this.localTimeStampMill - this.startTimeMill}ms`;
            console.log(`\x1b[42m\x1b[30m ${tag} \x1b[0m\x1b[32m ${this.toLogMessage1(...value)} \x1b[0m`, times);
        }
    }

    /**
     * 打印错误日志
     * @param value 可变参数
     */
    static logError(...value: any[]): void {
        // @ts-ignore
        console.error('\x1b[41m\x1b[37m Err ERROR \x1b[0m\x1b[31m ' + this.toLogMessage(...value) + '\x1b[0m');
        // if (Util.isLog){
        //     BigTips.error(this.toLogMessage(...value));
        // }
    }

    /**
     * 将数字转换为带千分位的字符串
     * @param v 数字值
     * @returns 带千分位的字符串
     */
    static toThousandsString(v: number): string {
        return v <= 0 ? "0" : v.toLocaleString();
    }

    /**
     * 将数字乘以10000后转换为带千分位的字符串
     * @param v 数字值
     * @returns 带千分位的字符串
     */
    static toThousandString(v: number): string {
        return this.toThousandAtomString(v * 10000);
    }

    /**
     * 将原子值（内部计算用）转换为带千分位的字符串
     * @param atomValue 数值
     * @param keepCount 保留的小数位数
     * @returns 带千分位的字符串
     */
    static toThousandAtomString(atomValue: number, keepCount: number = 3): string {
        if (atomValue === 0) return "0";
        if (atomValue > -10000 && atomValue < 10000) {
            return this.numberToString(atomValue * 0.0001, keepCount, true);
        }

        const intSection = Math.floor(atomValue / 10000);
        const intStr = intSection.toLocaleString();
        const delta = atomValue - intSection * 10000;
        if (delta === 0) {
            return intStr;
        }

        let decimalStr = this.numberToString(delta * 0.0001, keepCount, true);
        const i = decimalStr.indexOf(".");
        if (i !== -1) {
            decimalStr = decimalStr.slice(i);
            return intStr + decimalStr;
        }

        return intStr;
    }

    /**
     * 将时间戳转换为日期对象
     * @param secondTick 时间戳（秒）
     * @returns 日期对象
     */
    static timeStampToDate(secondTick: number): Date {
        return new Date(secondTick * 1000);
    }

    /**
     * 将日期对象转换为时间戳（秒）
     * @param date 日期对象
     * @returns 时间戳（秒）
     */
    static dateToTimeStamp(date: Date): number {
        return Math.floor(date.getTime() / 1000);
    }

    /**
     * 将日期对象转换为时间戳（毫秒）
     * @param date 日期对象
     * @returns 时间戳（毫秒）
     */
    static dateToTimeStampMill(date: Date): number {
        return date.getTime();
    }

    /**
     * 格式化时间戳为本地时间字符串
     * @param timeStamp 时间戳（秒）
     * @returns 本地时间字符串
     */
    static timeStampFormat(timeStamp: number): string {
        return this.timeStampToDate(timeStamp).toLocaleString();
    }

    /**
     * 格式化时间戳为本地日期字符串
     * @param timeStamp 时间戳（秒）
     * @returns 本地日期字符串
     */
    static timeStampFormatDate(timeStamp: number): string {
        return this.timeStampToDate(timeStamp).toLocaleDateString();
    }

    /**
     * 格式化时间戳为本地日期时间字符串
     */
    static timeStampToLocale(timeStamp: number): string {
        return this.timeStampToLocaleDate(timeStamp).toLocaleString();
    }

    /**
     * 格式化时间戳为本地日期时间字符串
     */
    static timeStampToLocaleDate(timeStamp: number): string {
        return new Date(timeStamp).toLocaleString();
    }

    /**
     * 将秒数转换为可读的时分秒格式
     * @param seconds 秒数
     * @param maxCount 最大显示部分
     * @returns 格式化后的字符串
     */
    static formatSeconds(seconds: number, maxCount: number = 3): string {
        const day = Math.floor(seconds / 86400);
        const hour = Math.floor((seconds % 86400) / 3600);
        const min = Math.floor((seconds % 3600) / 60);
        const sec = seconds % 60;
        const timeFormatStrList = ['d', 'h', 'm', 's'];
        let str = "";
        let index = 0;

        if (day > 0 && index < maxCount) {
            str += `${day}${timeFormatStrList[0]} `;
            index++;
        }

        if ((hour > 0 && index < maxCount) || (day > 0 && index < maxCount)) {
            str += `${hour}${timeFormatStrList[1]} `;
            index++;
        }

        if ((min > 0 && index < maxCount) || (hour > 0 && index < maxCount)) {
            str += `${min}${timeFormatStrList[2]} `;
            index++;
        }

        if ((sec > 0 && index < maxCount) || (min > 0 && index < maxCount)) {
            str += `${sec}${timeFormatStrList[3]} `;
            index++;
        }

        if (index === 0 && sec === 0) {
            str = `0${timeFormatStrList[3]}`;
        }

        return str.trim();
    }

    /**
     * 将秒数转换为包含天数的详细格式（例如 1天01:01:01）
     * @param seconds 秒数
     * @returns 格式化后的字符串
     */
    static formatSecondsWithDay(seconds: number): string {
        const day = Math.floor(seconds / 86400);
        const hour = Math.floor((seconds - day * 86400) / 3600);
        const min = Math.floor((seconds - day * 86400 - hour * 3600) / 60);
        const sec = seconds - day * 86400 - hour * 3600 - min * 60;
        const builder: string[] = [];

        if (day > 0) {
            builder.push(`${day}D `);
        }

        builder.push(hour < 10 ? `0${hour}` : `${hour}`);
        builder.push(':');
        builder.push(min < 10 ? `0${min}` : `${min}`);
        builder.push(':');
        builder.push(sec < 10 ? `0${sec}` : `${sec}`);

        return builder.join('');
    }

    /**
     * 将秒数转换为详细的可读格式（例如 1天1小时1分钟1秒）
     * @param seconds 秒数
     * @returns 格式化后的字符串
     */
    static formatSecondsWithDayDetailed(seconds: number): string {
        const day = Math.floor(seconds / 86400);
        const hour = Math.floor((seconds - day * 86400) / 3600);
        const min = Math.floor((seconds - day * 86400 - hour * 3600) / 60);
        const sec = seconds - day * 86400 - hour * 3600 - min * 60;
        const builder: string[] = [];

        if (day > 0) {
            builder.push(`${day} day`);
        }
        if (hour > 0) {
            builder.push(`${hour} hour`);
        }
        if (min > 0) {
            builder.push(`${min} min`);
        }
        if (sec > 0) {
            builder.push(`${sec} sec`);
        }

        return builder.join(' ');
    }

    /**
     * 将数组转换为字符串格式
     * @param values 数组
     * @returns 字符串格式
     */
    static listToStr(values: any[] | null): string {
        if (values == null) return "null";
        return `[${values.join(",")}]`;
    }

    /**
     * 将二维数组转换为字符串格式
     * @param values 二维数组
     * @returns 字符串格式
     */
    static listToStr2(values: any[][] | null): string {
        if (values == null) return "null";
        const sb: string[] = values.map(this.listToStr);
        return `[${sb.join(", ")}]`;
    }

    /**
     * 将数组中特定索引的值转换为字符串格式
     * @param values 二维数组
     * @param index 指定的索引
     * @returns 字符串格式
     */
    static list2ToStr(values: any[][] | null, index: number): string {
        if (values == null) return "null";
        return `[${values.map(arr => arr[index]).join(", ")}]`;
    }

    /**
     * 检查数组中是否包含某个元素
     * @param arr 数组
     * @param cashIndex 元素
     * @returns 是否包含
     */
    static hasArrayKey(arr: any[], cashIndex: any): boolean {
        return arr.includes(cashIndex);
    }

    /**
     * 自动将数值转换为美元字符串
     * @param value 数值
     * @returns 美元字符串
     */
    static toUsdStrAuto(value: number): string {
        return (value / 10000).toFixed(3);
    }

    /**
     * 随机打乱数组顺序
     * @param array 数组
     */
    static shuffle<T>(array: T[]): void {
        for (let i = array.length - 1; i > 0; i--) {
            const j: number = Math.floor(Math.random() * (i + 1));
            [array[i], array[j]] = [array[j], array[i]];
        }
    }

    /**
     * 忽略大小写比较两个字符串是否相等
     * @param str1 字符串1
     * @param str2 字符串2
     * @returns 是否相等
     */
    static equalsIgnoreCase(str1: string, str2: string): boolean {
        return str1.toLowerCase() === str2.toLowerCase();
    }

    /**
     * 尝试获取参数数组中的指定索引的值，若不存在则返回默认值
     * @param args 参数数组
     * @param index 索引
     * @param defaultValue 默认值
     * @returns 获取的值或默认值
     */
    static tryGetArgs<T>(args: any[] | null | undefined, index: number, defaultValue: T): T {
        if (!args || index < 0 || index >= args.length) {
            return defaultValue;
        }
        return args[index] !== undefined ? args[index] : defaultValue;
    }

    /**
     * 将时间戳转换为ISO时间格式字符串
     * @param timeSecond 时间戳（秒）
     * @returns ISO时间格式字符串
     */
    static timeLogStr(timeSecond: number): string {
        return new Date(timeSecond * 1000).toISOString().replace('T', ' ').split('.')[0];
    }

    /**
     * H5随机ID 每个浏览器都不一样
     */
    static get h5UUID(): string {


        const storedUUID = DataMgr.getStorage(masterConst.H5_UUID) as string | null;
        // 如果本地存储中存在有效的 UUID，直接返回
        if (typeof storedUUID === 'string' && storedUUID.length > 5) {
            return storedUUID;
        }

        if (this.newUUID) {
            // 将新的 UUID 储存在本地
            DataMgr.setStorage(masterConst.H5_UUID, this.newUUID);
            // 返回生成的 UUID
            return this.newUUID;
        }

        // 生成一个新的 UUID
        const newUUID = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c: string) => {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });

        // 将新的 UUID 储存在本地
        DataMgr.setStorage(masterConst.H5_UUID, newUUID);


        this.newUUID = newUUID;

        // 返回生成的 UUID
        return newUUID;
    }

    //获取时区
    static get GetTimeZone() {
        const offset = -new Date().getTimezoneOffset() / 60;
        return offset >= 0 ? `+${offset}` : `${offset}`;
    }

    /**
     * 获取当前页面的主机和路径
     */
    static get locationHost(): string {
        return `${location.host}${location.pathname}`;
    }

    /**
     * 发送消息给父级页面
     * @param data 消息数据
     */
    static sendMessageToParent(data: any): void {
        // const dataStr = JSON.stringify(data);
        window.parent.postMessage(data, '*'); // '*' 可以替换为特定的来源
    }

    // 类型检查方法

    /**
     * 判断对象是否为 undefined
     * @param obj 对象
     * @returns 是否为 undefined
     */
    static isUndefined(obj: any): obj is undefined {
        return obj === undefined;
    }

    /**
     * 判断对象是否为 null
     * @param obj 对象
     * @returns 是否为 null
     */
    static isNull(obj: any): obj is null {
        return obj === null;
    }

    /**
     * 判断对象是否为字符串
     * @param obj 对象
     * @returns 是否为字符串
     */
    static isString(obj: any): obj is string {
        return typeof obj === 'string';
    }

    /**
     * 判断对象是否为函数
     * @param obj 对象
     * @returns 是否为函数
     */
    static isFunction(obj: any): obj is Function {
        return typeof obj === 'function';
    }

    /**
     * 判断对象是否为对象
     * @param obj 对象
     * @returns 是否为对象
     */
    static isObject(obj: any): obj is object {
        return typeof obj === 'object' && obj !== null;
    }


    private static s_globalId = 0;
    static Deg2Rad = Math.PI / 180.0;
    static Rad2Deg = 180.0 / Math.PI;


    static toRadian(degree: number) {
        return degree * this.Deg2Rad;
    }

    static toDegree(radian: number) {
        return radian * this.Rad2Deg;
    }

    static hasFlag(target: number, flag: number) {
        return target & flag;
    }

    static isNullOrEmpty(str: string) {
        return str == null || str === "";
    }

    static arrayRemoveItem(array: any[], item: any) {
        let index = array.indexOf(item);
        if (index != -1) {
            array.splice(index, 1);
            return true;
        }
        return false;
    }

    static checkObjId(obj: object): string {
        // @ts-ignore
        let objId: string = obj.__s_id;
        if (!objId) {
            this.s_globalId++;
            // @ts-ignore
            obj.__s_id = objId;
        }
        return objId;
    }

    static distanceXY(x: number, y: number) {
        return Math.sqrt(x * x + y * y);
    }

    static dot_formats = [[1, 1], [10, 0.1], [100, 0.01], [1000, 0.001], [10000, 0.0001], [100000, 0.00001]];

    static numberToString(v: number, keepCount = 3, isTrimZero = true) {
        let f = this.dot_formats[keepCount];
        let d = v >= 0 ? 1e-14 : -1e-14;
        let result = (Math.trunc(v * f[0]) * f[1] + d).toFixed(keepCount);
        if (isTrimZero) {
            let isFloat = false;
            for (let i = 0; i < result.length; i++) {
                let c = result.charCodeAt(i);
                if (c == 46 || c == 44) {//46:.  44:,
                    isFloat = true;
                }
            }
            if (isFloat) {
                while (result.charCodeAt(result.length - 1) == 48) {//0
                    result = result.slice(0, -1);
                }
                if (result.length > 0) {
                    let lastCode = result.charCodeAt(result.length - 1);
                    if (lastCode == 46 || lastCode == 44) {
                        result = result.slice(0, -1);
                    }
                }
            }
        }
        return result;
    }

    static fixValue(v: number) {
        if (v >= 100) {
            return Math.floor(v).toFixed(0);
        } else if (v >= 10) {
            return this.numberToString(v, 1);
        } else {
            return this.numberToString(v, 2);
        }
    }

    private static UnitList = ['', 'K', 'M', 'B', 'T', 'Q'];

    static toKmbtUnitString(v: number) {
        let f = v < 0 ? -1 : 1;
        v = Math.abs(v);
        let result = "";
        if (v < 1000) {
            result = v.toString();
        } else if (v < 1000000) {
            result = this.fixValue(v * 0.001) + "K";
        } else if (v < 1000000000) {
            result = this.fixValue(v * 0.000001) + "M";
        } else if (v < 1000000000000) {
            result = this.fixValue(v * 0.000000001) + "B";
        } else if (v < 1000000000000000) {
            result = this.fixValue(v * 0.000000000001) + "T";
        } else {
            result = this.fixValue(v * 0.000000000000001) + "Q";
        }
        if (f < 0) {
            return "-" + result;
        }
        return result;
    }

    static toThousandsTagString(v: number) {
        return v.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    static isCurPathName(targetRoute: string, pathname: string) {
        const currentPath = pathname;

        if (targetRoute === "/") {
            return currentPath === targetRoute; // 主页精确匹配
        }

        if (targetRoute + '/' === currentPath) {
            return true;
        }

        return currentPath === targetRoute; // 其他路径匹配
    };

    public static NumberToString(value: number, keepCount: number = 2, isTrimZero: boolean = true): string {
        let str = value.toFixed(keepCount);
        if (isTrimZero) {
            str = str.replace(/0+$/, '');
            str = str.replace(/\.$/, '');
        }
        return str;
    }

    public static units = ["K", "M", "B", "T", "Q", "Qi", "S"]

    public static ToKMBTUnitString(value: number, decimals: number = 1): string {
        const absValue = Math.abs(value);
        if (absValue < 1000) {
            return value.toString();
        }
        const exponent = Math.floor(Math.log10(absValue) / 3);
        const unit = this.units[exponent - 1] || "Q";
        const scaled = value / Math.pow(1000, exponent);
        return `${scaled.toFixed(decimals)}${unit}`;
    }

    public static ToThousandsString(value: number): string {
        // 简单示例：实际可根据需要实现千分位格式化
        return value.toLocaleString();
    }

    //深拷贝功能
    public static DeepClone<T>(obj: T): T {
        return JSON.parse(JSON.stringify(obj));
    }

    /**
     * 根据枚举名称获取对应的枚举值。
     * @param enumObj - 枚举对象
     * @param key - 枚举的名称（字符串）
     * @returns 枚举值或 undefined 如果名称不存在
     */
    static getEnumName<T extends { [key: number]: string }>(enumObj: T, value: number): string | undefined {
        return enumObj[value];
    }

}
